import React, { useState, useEffect } from "react";
import { ArrowLeft, Loader, CheckCircle, AlertCircle, Phone } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import toast from "react-hot-toast";
import useEmergency from "../../hooks/useEmergency";
import {
  validateEmergencyAppointment,
  formatPhoneNumber,
  getCrisisSeverity,
  getSeverityColors,
  getRecommendedHotlines,
  requiresImmediateAction,
} from "../../utils/emergency.utils";

const EmergencyBookingForm = ({ selectedCrisis, onBack, onSuccess }) => {
  const { user } = useAuth();
  const { createEmergencyAppointment, loading, error, emergencyAppointment } = useEmergency();
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const [formData, setFormData] = useState({
    fullName: user?.fullName || "",
    email: user?.email || "",
    phone: user?.phone || "",
    notes: "",
    sessionType: "physical",
    emergencyContactName: "",
    emergencyContactPhone: "",
    emergencyContactRelation: "",
  });

  const severity = getCrisisSeverity(selectedCrisis.id);
  const severityColors = getSeverityColors(severity);
  const recommendedHotlines = getRecommendedHotlines(selectedCrisis.id);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors({ ...validationErrors, [name]: null });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationErrors({});

    // Prepare validation data
    const validationData = {
      crisisType: selectedCrisis.id,
      sessionType: formData.sessionType,
      userId: user?.id,
      guestName: formData.fullName,
      guestEmail: formData.email,
      guestPhone: formData.phone,
      emergencyContactName: formData.emergencyContactName,
      emergencyContactPhone: formData.emergencyContactPhone,
    };

    // Validate form data
    const validation = validateEmergencyAppointment(validationData);
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      toast.error("Please correct the form errors");
      return;
    }

    try {
      const payload = {
        crisisType: selectedCrisis.id,
        notes: formData.notes,
        sessionType: formData.sessionType,
        preferredTime: new Date().toISOString(),
      };

      // If user is authenticated, send clientId
      if (user) {
        payload.clientId = user.id;
      } else {
        // Guest booking - format phone numbers
        payload.guestInfo = {
          fullName: formData.fullName,
          email: formData.email,
          phone: formatPhoneNumber(formData.phone),
        };

        // Add emergency contact if provided
        if (formData.emergencyContactName && formData.emergencyContactPhone) {
          payload.guestInfo.emergencyContact = {
            name: formData.emergencyContactName,
            phone: formatPhoneNumber(formData.emergencyContactPhone),
            relationship: formData.emergencyContactRelation,
          };
        }
      }

      await createEmergencyAppointment(payload);
      setSuccess(true);
      toast.success("Emergency appointment created successfully!");

      setTimeout(() => {
        onSuccess();
      }, 5000);
    } catch (err) {
      console.error("Emergency booking error:", err);
      toast.error(err.message || "Failed to create emergency appointment");
    }
  };

  useEffect(() => {
    if (error) {
      toast.error(error.message || "An error occurred");
    }
  }, [error]);

  if (success && emergencyAppointment) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-teal-600 flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-10 h-10 text-white" />
        </div>
        <h3 className="text-2xl font-display font-bold text-gray-900 mb-2">
          Appointment Confirmed
        </h3>
        <p className="text-gray-600 mb-6">
          Your emergency appointment has been scheduled
        </p>

        <div className="bg-gradient-to-br from-green-50 to-teal-50 border-2 border-green-200 rounded-xl p-6 max-w-md mx-auto text-left shadow-lg">
          <div className="space-y-4">
            <div>
              <p className="text-xs uppercase font-semibold text-gray-500 tracking-wide">Counselor</p>
              <p className="font-semibold text-gray-900 mt-1">
                {emergencyAppointment.counselor?.fullName || "Assigned"}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase font-semibold text-gray-500 tracking-wide">Date & Time</p>
              <p className="font-semibold text-gray-900 mt-1">
                {new Date(emergencyAppointment.date).toLocaleDateString('en-US', {
                  weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                })} at {emergencyAppointment.timeSlot}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase font-semibold text-gray-500 tracking-wide">Session Type</p>
              <p className="font-semibold text-gray-900 capitalize mt-1">
                {emergencyAppointment.sessionType}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase font-semibold text-gray-500 tracking-wide">Location</p>
              <p className="font-semibold text-gray-900 mt-1">
                {emergencyAppointment.sessionType === "physical"
                  ? "Uzima Counseling Center"
                  : "Virtual (Link will be sent via email)"}
              </p>
            </div>
          </div>
        </div>

        {requiresImmediateAction(selectedCrisis.id) && recommendedHotlines.length > 0 && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg max-w-md mx-auto">
            <p className="text-sm font-semibold text-red-900 mb-2">
              If you need immediate help before your appointment:
            </p>
            {recommendedHotlines.map((hotline, idx) => (
              <a
                key={idx}
                href={`tel:${hotline.number.replace(/\s/g, '')}`}
                className="flex items-center gap-2 text-sm text-red-700 hover:text-red-900 font-medium"
              >
                <Phone className="w-4 h-4" />
                {hotline.name}: {hotline.number}
              </a>
            ))}
          </div>
        )}

        <p className="text-sm text-gray-500 mt-6">
          A confirmation email has been sent with all appointment details.
        </p>
      </div>
    );
  }

  return (
    <div>
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-gray-600 hover:text-uzima-maroon-700 mb-6 font-medium transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to crisis selection
      </button>

      {/* Selected Crisis Info with Severity Badge */}
      <div className={`${severityColors.bg} border-2 ${severityColors.border} rounded-xl p-5 mb-6 shadow-sm`}>
        <div className="flex items-start gap-3">
          <div className={`${severityColors.badge} rounded-lg p-2`}>
            <AlertCircle className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <p className={`font-display font-bold ${severityColors.text} text-lg`}>
                {selectedCrisis.label}
              </p>
              <span className={`${severityColors.badge} text-white text-xs px-2 py-1 rounded-full font-semibold uppercase`}>
                {severity}
              </span>
            </div>
            {requiresImmediateAction(selectedCrisis.id) && (
              <p className={`text-sm ${severityColors.text} font-semibold mb-3`}>
                This is a high-priority crisis. Help is available immediately.
              </p>
            )}
            <div className="space-y-1">
              <p className={`text-sm ${severityColors.text} font-medium`}>Emergency Hotlines Available 24/7:</p>
              {recommendedHotlines.map((hotline, idx) => (
                <a
                  key={idx}
                  href={`tel:${hotline.number.replace(/\s/g, '')}`}
                  className={`flex items-center gap-2 text-sm ${severityColors.text} hover:underline font-semibold`}
                >
                  <Phone className="w-4 h-4" />
                  {hotline.name}: {hotline.number}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Contact Information */}
        {!user && (
          <div className="space-y-4 bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h3 className="font-display font-bold text-gray-900 text-lg border-b border-gray-200 pb-2">
              Your Information
            </h3>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                required
                className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-uzima-maroon-500 focus:border-uzima-maroon-500 transition-all ${
                  validationErrors.guestName ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="Enter your full name"
              />
              {validationErrors.guestName && (
                <p className="text-red-600 text-sm mt-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {validationErrors.guestName}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-uzima-maroon-500 focus:border-uzima-maroon-500 transition-all ${
                  validationErrors.guestEmail ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="your.email@example.com"
              />
              {validationErrors.guestEmail && (
                <p className="text-red-600 text-sm mt-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {validationErrors.guestEmail}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Phone Number (Kenyan format) *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-uzima-maroon-500 focus:border-uzima-maroon-500 transition-all ${
                  validationErrors.guestPhone ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="0712345678 or +254712345678"
              />
              {validationErrors.guestPhone && (
                <p className="text-red-600 text-sm mt-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {validationErrors.guestPhone}
                </p>
              )}
            </div>

            {/* Emergency Contact */}
            <div className="pt-4 border-t border-gray-200">
              <h4 className="font-semibold text-gray-900 mb-3">
                Emergency Contact <span className="text-gray-500 font-normal">(Optional but recommended)</span>
              </h4>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Contact Name
                  </label>
                  <input
                    type="text"
                    name="emergencyContactName"
                    value={formData.emergencyContactName}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-uzima-maroon-500 focus:border-uzima-maroon-500 transition-all ${
                      validationErrors.emergencyContactName ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="Contact person's name"
                  />
                  {validationErrors.emergencyContactName && (
                    <p className="text-red-600 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {validationErrors.emergencyContactName}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Contact Phone
                  </label>
                  <input
                    type="tel"
                    name="emergencyContactPhone"
                    value={formData.emergencyContactPhone}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-uzima-maroon-500 focus:border-uzima-maroon-500 transition-all ${
                      validationErrors.emergencyContactPhone ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="0712345678 or +254712345678"
                  />
                  {validationErrors.emergencyContactPhone && (
                    <p className="text-red-600 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {validationErrors.emergencyContactPhone}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Relationship
                  </label>
                  <input
                    type="text"
                    name="emergencyContactRelation"
                    value={formData.emergencyContactRelation}
                    onChange={handleChange}
                    placeholder="e.g., Parent, Sibling, Friend"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-uzima-maroon-500 focus:border-uzima-maroon-500 transition-all"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Session Type */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            Preferred Session Type *
          </label>
          <div className="grid grid-cols-2 gap-4">
            <label
              className={`flex items-center justify-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                formData.sessionType === "physical"
                  ? "border-uzima-maroon-600 bg-gradient-to-br from-uzima-maroon-50 to-white shadow-md"
                  : "border-gray-300 hover:border-uzima-maroon-300 hover:shadow"
              }`}
            >
              <input
                type="radio"
                name="sessionType"
                value="physical"
                checked={formData.sessionType === "physical"}
                onChange={handleChange}
                className="w-4 h-4 text-uzima-maroon-600 mr-3"
              />
              <span className="font-semibold">In-Person</span>
            </label>
            <label
              className={`flex items-center justify-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                formData.sessionType === "virtual"
                  ? "border-uzima-maroon-600 bg-gradient-to-br from-uzima-maroon-50 to-white shadow-md"
                  : "border-gray-300 hover:border-uzima-maroon-300 hover:shadow"
              }`}
            >
              <input
                type="radio"
                name="sessionType"
                value="virtual"
                checked={formData.sessionType === "virtual"}
                onChange={handleChange}
                className="w-4 h-4 text-uzima-maroon-600 mr-3"
              />
              <span className="font-semibold">Virtual</span>
            </label>
          </div>
        </div>

        {/* Additional Notes */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Additional Information <span className="text-gray-500 font-normal">(Optional)</span>
          </label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows="4"
            placeholder="Any additional details that might help the counselor understand your situation better..."
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-uzima-maroon-500 focus:border-uzima-maroon-500 transition-all resize-none"
          />
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold py-4 rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-lg"
        >
          {loading ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              Creating Emergency Appointment...
            </>
          ) : (
            "Confirm Emergency Appointment"
          )}
        </button>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <p className="text-xs text-gray-600 text-center leading-relaxed">
            By submitting this form, you consent to emergency counseling services and data processing in accordance with our privacy policy. Your information is kept strictly confidential.
          </p>
        </div>
      </form>
    </div>
  );
};

export default EmergencyBookingForm;
